import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2,
  ChevronLeft, ChevronRight, Mic, Play, Save, Share2,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { AppLogo } from "@/components/AppLogo";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getYoutubeId(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function getInstagramId(url) {
  const m = url.match(/instagram\.com\/(?:p|reel)\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
}

/* ─── Promo Carousel ─── */
function PromoCarousel({ promotions }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (promotions.length <= 1) return;
    const t = setInterval(() => setCurrent((c) => (c + 1) % promotions.length), 6000);
    return () => clearInterval(t);
  }, [promotions.length]);

  if (promotions.length === 0) return <DefaultHero />;

  const promo = promotions[current];

  return (
    <div className="flex flex-col items-center justify-center h-full p-8 lg:p-12" data-testid="promo-carousel">
      {/* Media */}
      <div className="w-full max-w-md mb-6 animate-fade-in-up">
        {promo.media_type === "image" && promo.media_url && (
          <img
            src={promo.media_url}
            alt={promo.title}
            className="w-full rounded-2xl object-cover shadow-lg"
            style={{ maxHeight: 280 }}
          />
        )}
        {promo.media_type === "youtube" && promo.media_url && (
          <div className="w-full rounded-2xl overflow-hidden shadow-lg aspect-video">
            <iframe
              src={`https://www.youtube.com/embed/${getYoutubeId(promo.media_url)}`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={promo.title}
            />
          </div>
        )}
        {promo.media_type === "instagram" && promo.media_url && (
          <div className="w-full rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src={`https://www.instagram.com/p/${getInstagramId(promo.media_url)}/embed`}
              className="w-full"
              style={{ minHeight: 300 }}
              allowFullScreen
              title={promo.title}
            />
          </div>
        )}
      </div>

      {/* Text */}
      <div className="text-center max-w-md">
        <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3 font-heading leading-tight">
          {promo.title}
        </h2>
        {promo.description && (
          <p className="text-white/70 text-sm lg:text-base leading-relaxed mb-4">
            {promo.description}
          </p>
        )}
        {promo.link_url && (
          <a
            href={promo.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white font-medium text-sm transition-colors backdrop-blur-sm border border-white/20"
            data-testid="promo-carousel-link"
          >
            {promo.link_text || "Saiba mais"}
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* Dots */}
      {promotions.length > 1 && (
        <div className="flex items-center gap-3 mt-8">
          <button
            onClick={() => setCurrent((c) => (c - 1 + promotions.length) % promotions.length)}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-2">
            {promotions.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-2 rounded-full transition-all ${
                  i === current ? "w-8 bg-white" : "w-2 bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => setCurrent((c) => (c + 1) % promotions.length)}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Default hero when no promotions ─── */
function DefaultHero() {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 lg:p-12 text-center">
      <div className="flex items-center gap-4 mb-8">
        {[
          { icon: Mic, bg: "bg-rose-500/20", color: "text-rose-400" },
          { icon: Play, bg: "bg-emerald-500/20", color: "text-emerald-400" },
          { icon: Save, bg: "bg-amber-500/20", color: "text-amber-400" },
          { icon: Share2, bg: "bg-cyan-500/20", color: "text-cyan-400" },
        ].map(({ icon: Icon, bg, color }, i) => (
          <div
            key={i}
            className={`w-14 h-14 rounded-2xl ${bg} flex items-center justify-center animate-fade-in-up`}
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <Icon className={`w-7 h-7 ${color}`} />
          </div>
        ))}
      </div>
      <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 font-heading leading-tight">
        Capture suas ideias<br />por voz ou texto
      </h2>
      <p className="text-white/60 text-sm lg:text-base max-w-sm leading-relaxed">
        Escreva, grave, ouça e compartilhe. Tudo organizado em um só lugar com sincronização na nuvem.
      </p>
    </div>
  );
}

/* ─── Main Landing Page ─── */
export const LandingPage = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const [tab, setTab] = useState("login");
  const [promotions, setPromotions] = useState([]);

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) navigate("/app");
  }, [isAuthenticated, navigate]);

  // Fetch promotions
  useEffect(() => {
    axios.get(`${API_URL}/api/promotions/active`)
      .then((res) => setPromotions(res.data || []))
      .catch(() => {});
  }, []);

  const resetForm = () => {
    setName(""); setEmail(""); setPassword(""); setConfirmPassword("");
    setShowPassword(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Preencha todos os campos"); return; }
    setIsLoading(true);
    try {
      const result = await login(email, password);
      toast.success(`Bem-vindo, ${result.user.name}!`);
      if (result.migration?.migrated && result.migration.count > 0) {
        setTimeout(() => toast.success(`${result.migration.count} ideia(s) local(is) migrada(s)!`), 1000);
      }
      navigate("/app");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
    } finally { setIsLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name || !email || !password || !confirmPassword) { toast.error("Preencha todos os campos"); return; }
    if (password.length < 6) { toast.error("Senha deve ter pelo menos 6 caracteres"); return; }
    if (password !== confirmPassword) { toast.error("As senhas não coincidem"); return; }
    setIsLoading(true);
    try {
      const result = await register(name, email, password);
      toast.success(`Conta criada! Bem-vindo, ${result.user.name}!`);
      if (result.migration?.migrated && result.migration.count > 0) {
        setTimeout(() => toast.success(`${result.migration.count} ideia(s) migrada(s)!`), 1000);
      }
      navigate("/app");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar conta");
    } finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--vi-page)" }} data-testid="landing-page">
      {/* LEFT: Auth Form */}
      <div className="w-full lg:w-[480px] xl:w-[520px] shrink-0 flex flex-col justify-center px-6 md:px-12 lg:px-16 py-10">
        {/* Logo */}
        <div className="mb-10">
          <AppLogo size="lg" />
          <p className="text-sm mt-2" style={{ color: "var(--vi-text-muted)" }}>
            Capture, organize e ouça suas ideias
          </p>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 rounded-xl p-1" style={{ background: "var(--vi-input-bg)" }}>
          {[
            { id: "login", label: "Entrar" },
            { id: "register", label: "Criar Conta" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); resetForm(); }}
              className={`flex-1 py-2.5 text-sm font-medium rounded-lg transition-all ${
                tab === t.id
                  ? "bg-indigo-600 text-white shadow-sm"
                  : ""
              }`}
              style={tab !== t.id ? { color: "var(--vi-text-muted)" } : {}}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Login Form */}
        {tab === "login" && (
          <form onSubmit={handleLogin} className="space-y-4 animate-fade-in-up" data-testid="login-form">
            <div className="space-y-1.5">
              <Label style={{ color: "var(--vi-text-soft)" }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--vi-text-muted)" }} />
                <Input
                  type="email" placeholder="seu@email.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10" data-testid="login-email-input"
                  style={{ background: "var(--vi-input-bg)", border: "1px solid var(--vi-border-accent)", color: "var(--vi-text)" }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label style={{ color: "var(--vi-text-soft)" }}>Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--vi-text-muted)" }} />
                <Input
                  type={showPassword ? "text" : "password"} placeholder="--------"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10" data-testid="login-password-input"
                  style={{ background: "var(--vi-input-bg)", border: "1px solid var(--vi-border-accent)", color: "var(--vi-text)" }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--vi-text-muted)" }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={isLoading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11 mt-2" data-testid="login-submit-button">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Entrar <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
        )}

        {/* Register Form */}
        {tab === "register" && (
          <form onSubmit={handleRegister} className="space-y-4 animate-fade-in-up" data-testid="register-form">
            <div className="space-y-1.5">
              <Label style={{ color: "var(--vi-text-soft)" }}>Nome</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--vi-text-muted)" }} />
                <Input type="text" placeholder="Seu nome" value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10" data-testid="register-name-input"
                  style={{ background: "var(--vi-input-bg)", border: "1px solid var(--vi-border-accent)", color: "var(--vi-text)" }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label style={{ color: "var(--vi-text-soft)" }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--vi-text-muted)" }} />
                <Input type="email" placeholder="seu@email.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10" data-testid="register-email-input"
                  style={{ background: "var(--vi-input-bg)", border: "1px solid var(--vi-border-accent)", color: "var(--vi-text)" }}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label style={{ color: "var(--vi-text-soft)" }}>Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--vi-text-muted)" }} />
                <Input type={showPassword ? "text" : "password"} placeholder="Min. 6 caracteres"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10" data-testid="register-password-input"
                  style={{ background: "var(--vi-input-bg)", border: "1px solid var(--vi-border-accent)", color: "var(--vi-text)" }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--vi-text-muted)" }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label style={{ color: "var(--vi-text-soft)" }}>Confirmar Senha</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--vi-text-muted)" }} />
                <Input type="password" placeholder="--------"
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10" data-testid="register-confirm-password-input"
                  style={{ background: "var(--vi-input-bg)", border: "1px solid var(--vi-border-accent)", color: "var(--vi-text)" }}
                />
              </div>
            </div>

            <Button type="submit" disabled={isLoading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11 mt-2" data-testid="register-submit-button">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Criar Conta <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </form>
        )}

        {/* Footer links */}
        <div className="mt-8 space-y-2 text-center">
          <Link to="/app" className="text-sm block transition-colors" style={{ color: "var(--vi-text-muted)" }} data-testid="continue-without-login">
            Continuar sem conta →
          </Link>
          <Link to="/admin/login" className="text-xs text-indigo-500 hover:text-indigo-400 block transition-colors" data-testid="admin-login-link">
            Acesso administrador
          </Link>
        </div>
      </div>

      {/* RIGHT: Promo Area */}
      <div
        className="hidden lg:flex flex-1 relative"
        style={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)",
        }}
        data-testid="promo-area"
      >
        {/* Subtle decorative circles */}
        <div className="absolute top-20 right-20 w-64 h-64 rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full bg-purple-500/10 blur-3xl" />

        <PromoCarousel promotions={promotions} />
      </div>
    </div>
  );
};

export default LandingPage;
